import { ClearOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, InputRef, Table } from 'antd';
import type { ColumnsType, ColumnType, TableProps } from 'antd/es/table';
import { FilterDropdownProps } from 'antd/es/table/interface';
import { t } from 'i18next';
import { CSSProperties, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ColumnHighlight from 'src/app/components/ColumnHighlightComponent/ColumnHighlight';
import EllipsisTooltip from 'src/app/components/EllipsisTooltipComponent/EllipsisTooltip';
import styles from 'src/app/components/TableComponent/TableComponent.module.scss';
import { genericExcludeFilter } from 'src/app/helpers/permission.helper';
import { SidebarContext } from 'src/app/layouts/default/LayoutDefault';
import { useAppSelector } from 'src/app/redux/hook';
import { LAYOUTVIEW } from 'src/configs/system.config';
import { LanguagePreferenceEnum } from 'src/enums/language.enum';
import { LayoutViewType } from 'src/enums/user.enum';
import { FilterIcon } from 'src/icons';
import NoDataIcon from 'src/icons/NoDataIcon';
import { Table as TableTheme } from 'src/theme/Table.styled';
import tinycolor from 'tinycolor2';

export interface IColumnType<T> extends ColumnType<T> {
  tooltipText?: string | ReactNode;
  title?: string | ReactNode;
  children?: ColumnsType<T>;
  isOrderable?: boolean;
  excludeLayouts?: LayoutViewType[];
  editable?: boolean;
  searchable?: boolean;
}
export type CustomColumnsType<T> = IColumnType<T>[];

interface ITableComponentProps<T> extends TableProps<T> {
  extraContentWidth?: number;
  isModalTable?: boolean;
  columns: CustomColumnsType<T>;
  dataSource: TableProps<T>['dataSource'] | undefined;
  containerStyle?: CSSProperties;
  onTableReset?: () => void;
  tableRef?: any;
  lastClickedId?: string;
  onFilter?: (dataSource: any) => void;
}

// This component is used to create customazible table component
const TableComponent = <T extends { id?: string } & object>(props: ITableComponentProps<T>) => {
  const { extraContentWidth, isModalTable, containerStyle, onTableReset, tableRef, lastClickedId, ...tableProps } =
    props;

  const ref = useRef<any>();
  const searchInput = useRef<InputRef>(null);
  const { isCollapsed } = useContext(SidebarContext);
  const { theme } = useAppSelector((state) => state.configContent);
  const { preferredLanguage } = useAppSelector((state) => state.user);
  const collapseWidth = LAYOUTVIEW() === LayoutViewType.SAMA_BLACK_LAYOUT ? 0 : isCollapsed ? 200 : 360;
  const [tabWidth, setTabWidth] = useState<number>(window.innerWidth - collapseWidth - (extraContentWidth || 0));
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [showResetFilterBtn, setShowResetFilterBtn] = useState(false);

  const handleSearch = (selectedKeys: string[], confirm: FilterDropdownProps['confirm']) => {
    confirm();
  };

  // const getColumnSearchProps = (dataIndex: any): IColumnType<PersonIdentifiedColumnType> => ({
  //   filterDropdown: ({ selectedKeys, setSelectedKeys, confirm }) => (
  //     <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
  //       <Input
  //         placeholder={`Search ${dataIndex}`}
  //         value={selectedKeys[0]}
  //         onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
  //         style={{ marginBottom: 8, display: 'block' }}
  //         onPressEnter={() => handleSearch(selectedKeys as string[], confirm)}
  //       />
  //     </div>
  //   ),
  // });

  // Apply filters for columns and merge them recursively
  const mergedColumns: CustomColumnsType<T> = useMemo(() => {
    const mergeColumnsRecursively = (columns: IColumnType<T>[]): IColumnType<T>[] => {
      return columns.map((col) => {
        if (col.children) {
          return {
            ...col,

            children: mergeColumnsRecursively(col.children as IColumnType<T>[]),
          };
        }
        return {
          ...(col.searchable && {
            filterDropdown: ({ selectedKeys, setSelectedKeys, confirm }) => (
              <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                  ref={searchInput}
                  placeholder={`Search ${col.dataIndex}`}
                  value={selectedKeys[0]}
                  onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                  style={{ marginBottom: 8, display: 'block' }}
                  onPressEnter={() => handleSearch(selectedKeys as string[], confirm)}
                />
              </div>
            ),
            onFilterDropdownOpenChange(visible) {
              if (visible) setTimeout(() => searchInput.current?.select(), 100);
            },
          }),
          ...col,
          onCell: (record) => ({
            style: { borderBottom: `1px solid ${theme.colors.quoteBgc}` },
            ...(col.onCell && { ...col.onCell(record) }),
          }),
          title: col.tooltipText ? <ColumnHighlight col={col} /> : col.title,
          filterIcon: col.filterIcon
            ? col.filterIcon
            : col.searchable
              ? (filtered: boolean) => (
                  <SearchOutlined style={{ color: filtered ? theme.colors.quoteBgc : undefined, fontSize: '17px' }} />
                )
              : (filtered: boolean) => (filtered ? <FilterIcon color={theme.colors.quoteBgc} /> : <FilterIcon />),
          fixed: col?.fixed === 'right' && preferredLanguage === LanguagePreferenceEnum.AR ? 'left' : col.fixed,
          ...(!col.render && {
            render: (value: any) => <EllipsisTooltip>{value}</EllipsisTooltip>,
          }),
        };
      });
    };

    if (!tableProps.columns) return [];
    const filteredColumns = [...tableProps.columns].filter((col) => {
      let result = true;
      if (col.excludeLayouts) {
        result = genericExcludeFilter(col.excludeLayouts, LAYOUTVIEW());
      }
      return result;
    });

    return mergeColumnsRecursively(filteredColumns);
  }, [tableProps.columns]);

  const handleResize = () => {
    const newWidth = window.innerWidth - (extraContentWidth || 0) - collapseWidth;
    setTabWidth(newWidth);
  };

  // When extra content width changes update table with new width.
  useEffect(() => {
    handleResize();
  }, [extraContentWidth]);

  useEffect(() => {
    if (tableProps && tableProps.onFilter) {
      const { dataSource } = tableProps;

      if (dataSource && dataSource?.length > 0) tableProps.onFilter(dataSource?.map((data) => data.id));
    }
  }, [tableProps.dataSource]);

  // Update table's width when user resize browser
  useEffect(() => {
    let newWidth: number;
    if (!isFirstLoad) {
      if (isCollapsed) {
        newWidth = tabWidth + 185;
        setTabWidth(newWidth);
      } else {
        newWidth = tabWidth - 185;
        setTabWidth(newWidth);
      }
    }
    if (isFirstLoad) {
      setIsFirstLoad(false);
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isCollapsed]);

  const currentTheme = useAppSelector((state) => state.configContent.theme);

  // Calculate table's horizontal scroll width according to column count
  const calculateXWidth =
    mergedColumns.length > 12
      ? '150vw'
      : tabWidth > 1250
        ? undefined
        : mergedColumns.length >= 9
          ? '80vw'
          : tabWidth < 650 && mergedColumns.length >= 3
            ? '150vw'
            : props?.scroll?.x
              ? props?.scroll?.x
              : undefined;

  const [tableKey, setTableKey] = useState(0);

  // To reset all applied filters
  const resetFilters = () => {
    setShowResetFilterBtn(false);
    setTableKey((newKey) => newKey + 1);
    if (onTableReset) onTableReset();
  };
  return (
    <div style={containerStyle}>
      <TableTheme
        className={isModalTable ? styles.tableModal : styles.tableMain}
        theme={currentTheme}
        style={{ height: '100%' }}
      >
        <div
          id="table-container"
          style={{
            width: '100%',
            maxWidth: tabWidth,
            position: 'relative',
          }}
          ref={ref}
        >
          {/* Displaying remove all filters button */}
          {showResetFilterBtn ? (
            <Button
              className={styles.resetFilterBtn}
              style={{ background: theme.colors.quoteBgc }}
              onClick={resetFilters}
            >
              <ClearOutlined
                style={{
                  color: tinycolor(theme.colors.quoteBgc).isDark() ? '#fff' : '#000',
                  fontSize: '20px',
                  marginRight: '8px',
                  marginBottom: '10px',
                }}
              />
            </Button>
          ) : null}
          <Table
            ref={tableRef}
            key={tableKey}
            {...tableProps}
            onRow={(record) => ({
              ...tableProps.onRow,
              ...(tableProps.onRow && tableProps.onRow(record)),
              style: {
                cursor: tableProps?.onRow?.(record).onClick ? 'pointer' : 'default',
                background: !record.id
                  ? undefined
                  : record.id === lastClickedId
                    ? `${theme.colors.quoteBgc}40`
                    : undefined,
              },
            })}
            columns={mergedColumns}
            pagination={false}
            dataSource={tableProps.dataSource ?? []}
            // loading={!tableProps.dataSource}
            scroll={{
              scrollToFirstRowOnChange: tableProps.scroll?.scrollToFirstRowOnChange,
              y: tableProps.scroll ? tableProps.scroll.y : window.innerHeight - 400,
              x: calculateXWidth,
            }}
            onChange={(pagination, filters, sorter, extra) => {
              if (tableProps.onChange) tableProps.onChange(pagination, filters, sorter, extra);
              // Show clear filters button when any filtering is applied
              if (Object.values(filters).filter(Boolean).length) {
                setShowResetFilterBtn(true);
              } else setShowResetFilterBtn(false);
            }}
            locale={{
              triggerAsc: t('tables.media.informations.triggerAsc'),
              triggerDesc: t('tables.media.informations.triggerDesc'),
              cancelSort: t('tables.media.informations.cancelSort'),
              // Display custom no data view for empty table
              emptyText: tableProps.dataSource ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: tableProps.scroll ? `calc(${tableProps.scroll.y} - 20px)` : `${window.innerHeight - 450}px`,
                    borderBottom: `1px solid ${theme.colors.quoteBgc}`,
                  }}
                >
                  <NoDataIcon color={theme.colors.quoteBgc} />
                </div>
              ) : null,
            }}
            expandable={tableProps.expandable}
          />
        </div>
      </TableTheme>
    </div>
  );
};

export default TableComponent;
